// The engine-neutral half of the tiered snapshot load's incremental merge
// (SLICES.md § S7 items 1 + 2, rolled out to pg / mysql / oracle / sqlite by
// item 8). `mssqlSnapshotMerge.ts` is the hand-written original this
// generalizes; each of the other four engines wraps this module in a small
// `<engine>SnapshotMerge.ts` that supplies its own entry type, key and
// per-relation projection.
//
// Why a LIVE structure instead of re-running `project<Engine>Snapshot` per
// page: on a 20k-relation / 200k-column database a full projection is a
// multi-hundred-millisecond synchronous pass. Doing it once per page (dozens
// of pages) would be quadratic AND would blow the 50 ms long-task budget the
// perf gate (`snapshotLoadLongTasks.perf.spec.ts`) asserts. Here each page
// mutates only the relation entries it touches and re-projects only those, so
// the per-page main-thread cost scales with the PAGE, not with the database.

import type { SchemaSnapshot, TableMeta } from '@/core/engines/intellisenseTypes';

import { forEachChunkYielding } from './introspectionPaging';

/**
 * A snapshot under construction. `native` is the engine's own cache object,
 * `projected` its bus-shaped view; `entries[i]` and `projectedTables[i]` are
 * the same relation, which is what lets a page re-project only what it
 * touched. Whole-database arrays (FKs, routines, sequences …) are shared by
 * identity between `native` and `projected`, so appending to one is visible
 * through both.
 */
export type LiveSnapshot<TNative, TEntry> = {
  readonly native: TNative;
  readonly projected: SchemaSnapshot;
  /** Mutable array backing `projected.tables`. */
  readonly projectedTables: TableMeta[];
  /** Native relation entries, in `projectedTables` order. */
  readonly entries: TEntry[];
  /** Engine-folded relation key → index into `entries` / `projectedTables`. */
  readonly tableIndex: Map<string, number>;
  /** Per-artefact set of entry indexes this refresh has already replaced. */
  readonly replaced: Map<string, Set<number>>;
  /** Whole-database artefacts whose carried-over list this refresh replaced. */
  readonly replacedGlobals: Set<string>;
  readonly projectEntry: (entry: TEntry) => TableMeta;
};

/**
 * Build the live snapshot for a freshly fetched relation list, projecting the
 * relations in event-loop-yielding chunks so even a 200k-relation catalog
 * never occupies the main thread for a single long task.
 *
 * `buildProjected` receives the (mutable) projected-table array and returns the
 * bus snapshot around it — engines differ in which extra fields they carry
 * (search_path, extensions, attached databases …), so the shell stays theirs.
 */
export async function createLiveSnapshot<TNative, TEntry>(options: {
  readonly native: TNative;
  readonly entries: readonly TEntry[];
  readonly keyOf: (entry: TEntry) => string;
  readonly projectEntry: (entry: TEntry) => TableMeta;
  readonly buildProjected: (tables: TableMeta[]) => SchemaSnapshot;
  readonly yieldBetweenChunks?: () => Promise<void>;
}): Promise<LiveSnapshot<TNative, TEntry>> {
  const projectedTables: TableMeta[] = [];
  const entries: TEntry[] = [];
  const tableIndex = new Map<string, number>();
  await forEachChunkYielding(
    options.entries,
    (chunk) => {
      for (const entry of chunk) {
        tableIndex.set(options.keyOf(entry), entries.length);
        entries.push(entry);
        projectedTables.push(options.projectEntry(entry));
      }
    },
    { yieldBetweenChunks: options.yieldBetweenChunks },
  );

  return {
    native: options.native,
    projected: options.buildProjected(projectedTables),
    projectedTables,
    entries,
    tableIndex,
    replaced: new Map<string, Set<number>>(),
    replacedGlobals: new Set<string>(),
    projectEntry: options.projectEntry,
  };
}

/** Re-project one relation entry in place after a page mutated it. */
export function reprojectEntry<TNative, TEntry>(
  live: LiveSnapshot<TNative, TEntry>,
  index: number,
): void {
  const entry = live.entries[index];
  if (!entry) return;
  live.projectedTables[index] = live.projectEntry(entry);
}

function replacedSet<TNative, TEntry>(
  live: LiveSnapshot<TNative, TEntry>,
  artefact: string,
): Set<number> {
  const existing = live.replaced.get(artefact);
  if (existing) return existing;
  const created = new Set<number>();
  live.replaced.set(artefact, created);
  return created;
}

/**
 * Merge one page of per-relation items. Items are grouped by their relation,
 * then handed to `append` with `replace: true` the FIRST time this refresh
 * touches that relation for this artefact — which is how carried-over data
 * from the previous refresh is swapped out relation by relation rather than
 * blanked up front (stale-while-revalidate).
 *
 * Items whose relation is not in the snapshot are dropped: the relation list
 * is phase 1's, so anything else was created after it was read and will be
 * picked up by the next refresh.
 */
export function applyPerRelationPage<TNative, TEntry, TItem>(
  live: LiveSnapshot<TNative, TEntry>,
  artefact: string,
  items: readonly TItem[],
  spec: {
    readonly keyOf: (item: TItem) => string;
    readonly append: (entry: TEntry, items: TItem[], replace: boolean) => void;
  },
): void {
  if (items.length === 0) return;
  const grouped = new Map<number, TItem[]>();
  for (const item of items) {
    const index = live.tableIndex.get(spec.keyOf(item));
    if (index === undefined) continue;
    const list = grouped.get(index);
    if (list) list.push(item);
    else grouped.set(index, [item]);
  }
  const replaced = replacedSet(live, artefact);
  for (const [index, list] of grouped) {
    const entry = live.entries[index];
    if (!entry) continue;
    const replace = !replaced.has(index);
    replaced.add(index);
    spec.append(entry, list, replace);
    reprojectEntry(live, index);
  }
}

/**
 * After a per-relation artefact was read COMPLETELY (see `isCompleteOutcome`):
 * every relation this refresh did not touch for `artefact` has, by
 * construction, no rows of it any more — its carried-over items are stale
 * (the user dropped the table's last CHECK constraint / index outside this
 * app) and are cleared here. `clear` receives an entry only when it still
 * carries something to clear; touched entries and entries `clear` reports as
 * already empty are left alone. Never call this after a failed, aborted or
 * capped fetch — absence of rows then proves nothing (release review 1.5.0, F5).
 */
export function reconcilePerRelationArtefact<TNative, TEntry>(
  live: LiveSnapshot<TNative, TEntry>,
  artefact: string,
  clear: (entry: TEntry) => boolean,
): void {
  const replaced = live.replaced.get(artefact);
  for (let index = 0; index < live.entries.length; index += 1) {
    if (replaced?.has(index)) continue;
    const entry = live.entries[index];
    if (!entry || !clear(entry)) continue;
    reprojectEntry(live, index);
  }
  replacedSet(live, artefact);
}

/**
 * Whole-database artefacts: the FIRST page of a refresh drops the carried-over
 * list (so a deleted FK / routine / type disappears), later pages append. A
 * fetch that fails before its first page leaves the carried list standing —
 * stale beats empty for a completion vocabulary. A fetch that SUCCEEDS with
 * zero rows reaches here with `items = []` (the pager guarantees one sink
 * call) and clears the carried list, which is the whole point.
 */
export function applyGlobalPage<TNative, TEntry, TItem>(
  live: LiveSnapshot<TNative, TEntry>,
  artefact: string,
  target: TItem[],
  items: readonly TItem[],
): void {
  if (!live.replacedGlobals.has(artefact)) {
    live.replacedGlobals.add(artefact);
    target.length = 0;
  }
  target.push(...items);
}

/**
 * True the first time `artefact` is seen this refresh — for a global whose
 * target is a Record rather than an array, which the caller then clears
 * itself. Same "replace on the first page, append after" contract as
 * {@link applyGlobalPage}.
 */
export function claimGlobalReplace<TNative, TEntry>(
  live: LiveSnapshot<TNative, TEntry>,
  artefact: string,
): boolean {
  if (live.replacedGlobals.has(artefact)) return false;
  live.replacedGlobals.add(artefact);
  return true;
}
